import { useState, useMemo, useEffect, useCallback } from "react";
import { ArrowUpDown } from "lucide-react";
import { AssignIcon } from "./icons/patientListIcons";
import { useNavigate } from "react-router-dom";
import AssignPatientModal from "./AssignPatientModal";
import {
  PAGINATION,
  SORT_OPTIONS,
  FILTER_OPTIONS,
  CSS_CLASSES,
  getPatientListColumns,
} from "../constants/app";
import { patientUtils, riskUtils, storage, arrayUtils } from "../utils";
import type { Patient, SortState, PaginationState } from "../types";

interface DataTableProps {
  data: Patient[];
  searchQuery?: string;
  riskLevelFilter?: string;
  ageGroupFilter?: string;
  genderFilter?: string;
  userEmail?: string;
  isAdmin?: boolean;
  userId?: string;
  onPatientListRefresh?: () => void;
}

type SortField = "name" | "age" | "lastSync" | "bloodPressure" | "riskLevel";

export default function DataTable({
  data,
  searchQuery = "",
  riskLevelFilter = FILTER_OPTIONS.ALL,
  ageGroupFilter = FILTER_OPTIONS.ALL,
  genderFilter = FILTER_OPTIONS.ALL,
  isAdmin = false,
  onPatientListRefresh,
}: DataTableProps) {
  const navigate = useNavigate();

  // State for assignment modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<{ id: string; name: string; hospitalId?: string } | null>(null);

  // Use structured state for better organization
  const [sortState, setSortState] = useState<SortState>({
    field: SORT_OPTIONS.FIELDS.RISK_LEVEL,
    direction: SORT_OPTIONS.DIRECTIONS.DESC,
  });

  const [paginationState, setPaginationState] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    recordsPerPage: PAGINATION.RECORDS_PER_PAGE,
    totalRecords: 0,
  });

  // Mark as read in localStorage when navigating to patient details
  const handleNavigate = useCallback(
    (patientId: string) => {
      try {
        const readPatients = JSON.parse(storage.get("readPatients") || "{}");
        readPatients[patientId] = Math.floor(Date.now() / 1000); // store as seconds
        storage.set("readPatients", JSON.stringify(readPatients));
        // Store which patient we're navigating to
        storage.set("visitedPatientId", patientId);
        navigate(`/patient/${patientId}`);
      } catch (error) {
        // Fallback to direct navigation if localStorage fails
        navigate(`/patient/${patientId}`);
      }
    },
    [navigate]
  );

  // Filter and search logic using utility functions
  const filteredData = useMemo(() => {
    return data.filter((patient) => {
      const fullName = patientUtils.getFullName(patient);
      const gender = patientUtils.getGender(patient);
      const riskLevel = patientUtils.getRiskLevel(patient);
      const age = patientUtils.getAge(patient);
      const dob = patient.personal_information?.dob || "";

      // Skip patients with empty names
      if (!fullName || fullName.trim() === "") {
        return false;
      }

      // Search filter - only on Patient name, dob, and risk level
      const matchesSearch =
        searchQuery === "" ||
        fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dob.includes(searchQuery) ||
        riskLevel.toLowerCase().includes(searchQuery.toLowerCase());

      // Risk level filter
      const matchesRiskLevel =
        riskLevelFilter === FILTER_OPTIONS.ALL ||
        riskLevel.toLowerCase() === riskLevelFilter.toLowerCase();

      // Gender filter
      const matchesGender =
        genderFilter === FILTER_OPTIONS.ALL ||
        gender.toLowerCase() === genderFilter.toLowerCase();

      // Age group filter using utility function
      const matchesAgeGroup =
        ageGroupFilter === FILTER_OPTIONS.ALL ||
        (() => {
          switch (ageGroupFilter) {
            case "18-35":
              return age >= 18 && age <= 35;
            case "36-50":
              return age >= 36 && age <= 50;
            case "51-65":
              return age >= 51 && age <= 65;
            case "65+":
              return age > 65;
            default:
              return true;
          }
        })();

      return (
        matchesSearch && matchesRiskLevel && matchesAgeGroup && matchesGender
      );
    });
  }, [data, searchQuery, riskLevelFilter, ageGroupFilter, genderFilter]);

  // Optimized sort logic using utility functions
  const sortedData = useMemo(() => {
    return arrayUtils.sortBy(
      filteredData,
      (patient) => {
        switch (sortState.field) {
          case SORT_OPTIONS.FIELDS.NAME:
            return patientUtils.getFullName(patient).toLowerCase();
          case SORT_OPTIONS.FIELDS.AGE:
            return patientUtils.getAge(patient);
          case SORT_OPTIONS.FIELDS.LAST_SYNC:
            return patient.score_summary?.tested_at || "";
          case SORT_OPTIONS.FIELDS.RISK_LEVEL:
            const riskOrder = { good: 1, average: 2, poor: 3 };
            const riskLevel = patientUtils.getRiskLevel(patient).toLowerCase();
            return riskOrder[riskLevel as keyof typeof riskOrder] || 0;
          default:
            return patientUtils.getRiskLevel(patient);
        }
      },
      sortState.direction
    );
  }, [filteredData, sortState]);

  // Optimized pagination logic
  const paginatedData = useMemo(() => {
    const totalPages = Math.ceil(
      sortedData.length / paginationState.recordsPerPage
    );
    const startIndex =
      (paginationState.currentPage - 1) * paginationState.recordsPerPage;
    const endIndex = startIndex + paginationState.recordsPerPage;

    // Update pagination state if needed
    if (
      paginationState.totalPages !== totalPages ||
      paginationState.totalRecords !== sortedData.length
    ) {
      setPaginationState((prev) => ({
        ...prev,
        totalPages,
        totalRecords: sortedData.length,
      }));
    }

    return sortedData.slice(startIndex, endIndex);
  }, [
    sortedData,
    paginationState.currentPage,
    paginationState.recordsPerPage,
    paginationState.totalPages,
    paginationState.totalRecords,
  ]);

  // Memoized handlers for better performance
  const handleSort = useCallback((field: string) => {
    setSortState((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === SORT_OPTIONS.DIRECTIONS.ASC
          ? SORT_OPTIONS.DIRECTIONS.DESC
          : SORT_OPTIONS.DIRECTIONS.ASC,
    }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setPaginationState((prev) => ({ ...prev, currentPage: page }));
  }, []);

  // Reset to first page when data changes
  useEffect(() => {
    setPaginationState((prev) => ({ ...prev, currentPage: 1 }));
  }, [searchQuery, riskLevelFilter, ageGroupFilter, genderFilter, data]);

  const formatLastSync = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const SortIcon = useCallback(
    ({ field }: { field: SortField }) => {
      if (sortState.field !== field) {
        return <ArrowUpDown className="h-4 w-4 text-guardian-header opacity-60" />;
      }
      return <ArrowUpDown className="h-4 w-4 text-guardian-header" />;
    },
    [sortState]
  );

  const actionFabClass = CSS_CLASSES.PATIENT_LIST_TABLE.ACTION_FAB;

  const tableColumns = useMemo(
    () => getPatientListColumns(isAdmin),
    [isAdmin]
  );
  const tableColSpan = isAdmin ? 11 : 10;

  const getLatestVital = useCallback((patient: Patient, bodyType: string) => {
    if (!patient.vitals) return undefined;
    let all: any[] = [];
    Object.values(patient.vitals).forEach((arr: any) => {
      if (Array.isArray(arr)) {
        all = all.concat(arr.filter((v: any) => v.bodyType === bodyType));
      }
    });
    if (all.length === 0) return undefined;
    all.sort(
      (a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    return all[0];
  }, []);

  const getPatientSummary = useCallback(
    (patient: Patient) => {
      const fullName = patient.personal_information?.full_name || "-";
      const gender = patient.personal_information?.gender || "-";
      const riskLevel = patient.score_summary?.risk_level || "-";
      const bpVital = getLatestVital(patient, "BLOOD_PRESSURE");
      const hrVital = getLatestVital(patient, "HEART_RATE");
      const boVital = getLatestVital(patient, "BLOOD_OXYGEN");
      const stepsVital = getLatestVital(patient, "STEPS");

      const bloodPressure =
        bpVital?.body?.systolic && bpVital?.body?.diastolic
          ? `${bpVital.body.systolic}/${bpVital.body.diastolic}`
          : "-";
      const heartRate = hrVital?.body?.value || "-";
      const bloodOxygen = boVital?.body?.value || "-";
      const steps = stepsVital?.body?.value || "-";

      return {
        fullName,
        gender:
          gender === "-"
            ? gender
            : gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase(),
        riskLevel,
        bloodPressure,
        heartRate,
        bloodOxygen,
        steps,
        sleep: riskUtils.formatSleepDuration(
          patient.score_summary?.inputSummary?.sleep
        ),
        age: patientUtils.getAge(patient) || "-",
        lastSync: patient.score_summary?.tested_at,
        bpVital,
      };
    },
    [getLatestVital]
  );

  const renderMobilePatientCard = (patient: Patient) => {
    const patientId = patient.userId;
    const summary = getPatientSummary(patient);
    const riskDisplay = riskUtils.getRiskDisplay(summary.riskLevel);
    const vitalClass =
      summary.riskLevel && String(summary.riskLevel) !== "-"
        ? riskDisplay.vitalTextClass
        : "text-gray-500";

    return (
      <div
        key={patientId}
        role="link"
        tabIndex={0}
        className="rounded-2xl border border-guardian-20 bg-white p-3 cursor-pointer"
        onClick={() => handleNavigate(patientId)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleNavigate(patientId);
          }
        }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-gray-900">
              {summary.fullName}
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              {summary.age} years • {summary.gender}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span
              className={`h-5 rounded-[6px] px-1.5 text-xs font-normal text-white min-w-[52px] inline-flex items-center justify-center ${riskDisplay.badgeClass}`}
            >
              {riskDisplay.label}
            </span>
            {isAdmin && (
              <button
                type="button"
                className={`${actionFabClass} shrink-0`}
                title="Assign To Hospital"
                aria-label="Assign To Hospital"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedPatient({
                    id: patientId,
                    name: summary.fullName,
                    hospitalId: patient.hospital_id,
                  });
                  setAssignModalOpen(true);
                }}
              >
                <AssignIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg bg-guardian-10 p-2">
            <p className="text-xs text-guardian-header">Heart Rate</p>
            <p
              className={`mt-0.5 text-sm font-normal text-guardian-body ${
                summary.heartRate === "-"
                  ? "text-gray-500"
                  : vitalClass
              }`}
            >
              {summary.heartRate === "-" ? "-" : summary.heartRate}
            </p>
          </div>
          <div className="rounded-lg bg-guardian-10 p-2">
            <p className="text-xs text-guardian-header">Blood Pressure</p>
            <p
              className={`mt-0.5 text-sm font-normal text-guardian-body ${
                summary.bloodPressure === "-"
                  ? "text-gray-500"
                  : vitalClass
              }`}
            >
              {summary.bloodPressure}
            </p>
          </div>
          <div className="rounded-lg bg-guardian-10 p-2">
            <p className="text-xs text-guardian-header">Blood Oxygen</p>
            <p
              className={`mt-0.5 text-sm font-normal text-guardian-body ${
                summary.bloodOxygen === "-"
                  ? "text-gray-500"
                  : vitalClass
              }`}
            >
              {summary.bloodOxygen === "-" ? "-" : `${summary.bloodOxygen}%`}
            </p>
          </div>
          <div className="rounded-lg bg-guardian-10 p-2">
            <p className="text-xs text-guardian-header">Sleep</p>
            <p className="mt-0.5 text-sm font-semibold text-guardian-body">
              {summary.sleep}
            </p>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between rounded-lg bg-guardian-10 px-2 py-1.5 text-xs">
          <span className="text-guardian-header">Last Sync</span>
          <span className="font-semibold text-guardian-body">
            {formatLastSync(summary.lastSync)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className={`${CSS_CLASSES.PATIENT_LIST_TABLE.CARD} min-h-[420px] flex flex-col`}>
      <div className="md:hidden flex-1 overflow-y-auto p-2 space-y-2 min-h-[200px]">
        {paginatedData.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
            {data.length === 0
              ? "No patients found"
              : "No patients found matching your criteria"}
          </div>
        ) : (
          paginatedData.map(renderMobilePatientCard)
        )}
      </div>
      <div className={CSS_CLASSES.PATIENT_LIST_TABLE.WRAPPER}>
        <table className={CSS_CLASSES.PATIENT_LIST_TABLE.TABLE}>
          <colgroup>
            {tableColumns.map((col) => (
              <col key={col.id} style={{ width: `${col.widthPercent}%` }} />
            ))}
          </colgroup>
          <thead className="sticky top-0 z-10 bg-white">
            <tr className={CSS_CLASSES.PATIENT_LIST_TABLE.HEADER_ROW}>
              <th
                className={`${CSS_CLASSES.PATIENT_LIST_TABLE.HEADER_CELL} cursor-pointer hover:bg-guardian-10`}
                onClick={() => handleSort("name")}
              >
                <div className="flex h-11 items-center gap-2 min-w-0">
                  <span className="truncate">Patient Name</span>
                  <SortIcon field="name" />
                </div>
              </th>
              <th
                className={`${CSS_CLASSES.PATIENT_LIST_TABLE.HEADER_CELL} cursor-pointer hover:bg-guardian-10`}
                onClick={() => handleSort("age")}
              >
                <div className="flex h-11 items-center gap-2">
                  <span>Age</span>
                  <SortIcon field="age" />
                </div>
              </th>
              <th className={CSS_CLASSES.PATIENT_LIST_TABLE.HEADER_CELL_STATIC}>
                <span className="flex h-11 items-center truncate">Gender</span>
              </th>
              <th
                className={`${CSS_CLASSES.PATIENT_LIST_TABLE.HEADER_CELL} cursor-pointer hover:bg-guardian-10`}
                onClick={() => handleSort("riskLevel")}
              >
                <div className="flex h-11 items-center gap-2 min-w-0">
                  <span className="truncate">Risk Level</span>
                  <SortIcon field="riskLevel" />
                </div>
              </th>
              <th className={CSS_CLASSES.PATIENT_LIST_TABLE.HEADER_CELL_STATIC}>
                <span className="flex h-11 items-center truncate">
                  Heart Rate BPM
                </span>
              </th>
              <th className={CSS_CLASSES.PATIENT_LIST_TABLE.HEADER_CELL_STATIC}>
                <span className="flex h-11 items-center truncate">
                  Blood Pressure mmHg
                </span>
              </th>
              <th className={CSS_CLASSES.PATIENT_LIST_TABLE.HEADER_CELL_STATIC}>
                <span className="flex h-11 items-center truncate">
                  Blood Oxygen
                </span>
              </th>
              <th className={CSS_CLASSES.PATIENT_LIST_TABLE.HEADER_CELL_STATIC}>
                <span className="flex h-11 items-center">Sleep</span>
              </th>
              <th className={CSS_CLASSES.PATIENT_LIST_TABLE.HEADER_CELL_STATIC}>
                <span className="flex h-11 items-center">Steps</span>
              </th>
              <th
                className={`${CSS_CLASSES.PATIENT_LIST_TABLE.HEADER_CELL} cursor-pointer hover:bg-guardian-10`}
                onClick={() => handleSort("lastSync")}
              >
                <div className="flex h-11 items-center gap-2 min-w-0">
                  <span className="truncate">Last Sync</span>
                  <SortIcon field="lastSync" />
                </div>
              </th>
              {isAdmin && (
                <th className={CSS_CLASSES.PATIENT_LIST_TABLE.HEADER_CELL_ACTIONS}>
                  <span className="flex h-11 items-center">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white">
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={tableColSpan} className="px-6 py-12 text-center">
                  <p className="text-gray-500">
                    {data.length === 0
                      ? "No patients found"
                      : "No patients found matching your criteria"}
                  </p>
                </td>
              </tr>
            ) : (
              paginatedData.map((patient) => {
                const fullName = patient.personal_information?.full_name || "-";
                const gender = patient.personal_information?.gender || "-";
                const riskLevel = patient.score_summary?.risk_level || "-";

                // Helper to get latest vital by bodyType
                function getLatestVital(bodyType: string) {
                  if (!patient.vitals) return undefined;
                  let all: any[] = [];
                  // patient.vitals is an object with moduleId keys, each value is an array
                  Object.values(patient.vitals).forEach((arr: any) => {
                    if (Array.isArray(arr)) {
                      all = all.concat(
                        arr.filter((v: any) => v.bodyType === bodyType)
                      );
                    }
                  });
                  if (all.length === 0) return undefined;
                  all.sort(
                    (a: any, b: any) =>
                      new Date(b.created_at).getTime() -
                      new Date(a.created_at).getTime()
                  );
                  return all[0];
                }

                // Extract vitals or show "-" when not available
                const bpVital = getLatestVital("BLOOD_PRESSURE");
                const hrVital = getLatestVital("HEART_RATE");
                const boVital = getLatestVital("BLOOD_OXYGEN");
                const stepsVital = getLatestVital("STEPS");

                // Blood Pressure
                let bloodPressure = "-";
                if (
                  bpVital &&
                  bpVital.body &&
                  bpVital.body.systolic &&
                  bpVital.body.diastolic
                ) {
                  bloodPressure = `${bpVital.body.systolic}/${bpVital.body.diastolic}`;
                }

                // Heart Rate
                const heartRate =
                  hrVital && hrVital.body && hrVital.body.value
                    ? hrVital.body.value
                    : "-";

                // Blood Oxygen
                const bloodOxygen =
                  boVital && boVital.body && boVital.body.value
                    ? boVital.body.value
                    : "-";

                // Steps
                const steps =
                  stepsVital && stepsVital.body && stepsVital.body.value
                    ? stepsVital.body.value
                    : "-";

                const sleep = riskUtils.formatSleepDuration(
                  patient.score_summary?.inputSummary?.sleep
                );
                const riskDisplay = riskUtils.getRiskDisplay(riskLevel);
                const vitalClass =
                  riskLevel && String(riskLevel) !== "-"
                    ? riskDisplay.vitalTextClass
                    : "text-gray-500";
                // Use utility function for age calculation
                const age = patientUtils.getAge(patient) || "-";
                // Last Sync
                const lastSync = patient.score_summary?.tested_at;
                // Use patient.userId for navigation
                const patientId = patient.userId;
                // Use helpers for color and formatting
                return (
                  <tr
                    key={patientId}
                    className={`${CSS_CLASSES.PATIENT_LIST_TABLE.BODY_ROW} cursor-pointer`}
                    onClick={() => handleNavigate(patientId)}
                    role="link"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleNavigate(patientId);
                      }
                    }}
                  >
                    <td className={CSS_CLASSES.PATIENT_LIST_TABLE.BODY_CELL}>
                      <span className="block truncate">{fullName}</span>
                    </td>
                    <td className={CSS_CLASSES.PATIENT_LIST_TABLE.BODY_CELL}>
                      {age}
                    </td>
                    <td className={CSS_CLASSES.PATIENT_LIST_TABLE.BODY_CELL}>
                      <span className="block truncate">
                        {gender.charAt(0).toUpperCase() +
                          gender.slice(1).toLowerCase()}
                      </span>
                    </td>
                    <td className={CSS_CLASSES.PATIENT_LIST_TABLE.BODY_CELL_BADGE}>
                      <span
                        className={`inline-flex h-6 min-w-[57px] items-center justify-center rounded-[6px] px-1.5 text-sm font-normal text-white ${riskDisplay.badgeClass}`}
                      >
                        {riskDisplay.label}
                      </span>
                    </td>
                    <td className={CSS_CLASSES.PATIENT_LIST_TABLE.BODY_CELL}>
                      {heartRate === "-" ? (
                        <span className="text-gray-500">{heartRate}</span>
                      ) : (
                        <span className={vitalClass}>{heartRate}</span>
                      )}
                    </td>
                    <td className={CSS_CLASSES.PATIENT_LIST_TABLE.BODY_CELL}>
                      {bloodPressure === "-" ? (
                        <span className="block truncate text-gray-500">
                          {bloodPressure}
                        </span>
                      ) : (
                        <span className={`block truncate ${vitalClass}`}>
                          {bloodPressure}
                        </span>
                      )}
                    </td>
                    <td className={CSS_CLASSES.PATIENT_LIST_TABLE.BODY_CELL}>
                      {bloodOxygen === "-" ? (
                        <span className="text-gray-500">{bloodOxygen}</span>
                      ) : (
                        <span className={vitalClass}>{bloodOxygen}%</span>
                      )}
                    </td>
                    <td className={CSS_CLASSES.PATIENT_LIST_TABLE.BODY_CELL}>
                      <span className="block truncate">{sleep}</span>
                    </td>
                    <td className={CSS_CLASSES.PATIENT_LIST_TABLE.BODY_CELL}>
                      {steps === "-" ? steps : steps.toLocaleString()}
                    </td>
                    <td className={CSS_CLASSES.PATIENT_LIST_TABLE.BODY_CELL}>
                      {formatLastSync(lastSync)}
                    </td>
                    {isAdmin && (
                      <td
                        className={CSS_CLASSES.PATIENT_LIST_TABLE.BODY_CELL_ACTIONS}
                      >
                        <div className="flex h-11 items-center">
                          <button
                            type="button"
                            className={actionFabClass}
                            title="Assign To Hospital"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPatient({
                                id: patientId,
                                name: fullName,
                                hospitalId: patient.hospital_id,
                              });
                              setAssignModalOpen(true);
                            }}
                          >
                            <AssignIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {sortedData.length > 0 && (
        <div className="border-t border-guardian-20 bg-white px-4 py-3 md:px-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          {/* Showing X to Y of Z patients */}
          <div className="flex items-center justify-center md:justify-start">
            <p className="text-xs text-guardian-header md:text-sm">
              Showing{" "}
              <span className="font-medium">
                {Math.min(
                  (paginationState.currentPage - 1) *
                    paginationState.recordsPerPage +
                    1,
                  sortedData.length
                )}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(
                  paginationState.currentPage * paginationState.recordsPerPage,
                  sortedData.length
                )}
              </span>{" "}
              of <span className="font-medium">{sortedData.length}</span>{" "}
              patients
            </p>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-center space-x-2">
            {/* First Page Button */}
            <button
              onClick={() => handlePageChange(1)}
              disabled={
                paginationState.currentPage === 1 || sortedData.length === 0
              }
              className={`hidden md:block p-2 rounded-md text-sm ${
                paginationState.currentPage === 1 || sortedData.length === 0
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-guardian-80 hover:text-guardian-body"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Previous Button */}
            <button
              onClick={() =>
                handlePageChange(Math.max(paginationState.currentPage - 1, 1))
              }
              disabled={
                paginationState.currentPage === 1 || sortedData.length === 0
              }
              className={`flex min-h-10 items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium ${
                paginationState.currentPage === 1 || sortedData.length === 0
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-guardian-80 hover:text-guardian-body"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span className="hidden sm:inline">Previous</span>
            </button>

            {/* Current Page Input */}
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={paginationState.currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value);
                  if (page >= 1 && page <= paginationState.totalPages) {
                    handlePageChange(page);
                  }
                }}
                className="h-8 w-10 px-2 py-1 text-center text-sm border border-guardian-20 rounded-md focus:outline-none focus:ring-2 focus:ring-guardian-20 focus:border-transparent"
                min="1"
                max={paginationState.totalPages}
              />
              <span className="text-sm text-guardian-header">
                of {paginationState.totalPages}
              </span>
            </div>

            {/* Next Button */}
            <button
              onClick={() =>
                handlePageChange(
                  Math.min(
                    paginationState.currentPage + 1,
                    paginationState.totalPages
                  )
                )
              }
              disabled={
                paginationState.currentPage === paginationState.totalPages ||
                sortedData.length === 0
              }
              className={`flex min-h-10 items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium ${
                paginationState.currentPage === paginationState.totalPages ||
                sortedData.length === 0
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-guardian-80 hover:text-guardian-body"
              }`}
            >
              <span className="hidden sm:inline">Next</span>
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Last Page Button */}
            <button
              onClick={() => handlePageChange(paginationState.totalPages)}
              disabled={
                paginationState.currentPage === paginationState.totalPages ||
                sortedData.length === 0
              }
              className={`hidden md:block p-2 rounded-md text-sm ${
                paginationState.currentPage === paginationState.totalPages ||
                sortedData.length === 0
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-guardian-80 hover:text-guardian-body"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Assign Patient Modal */}
      {selectedPatient && (
        <AssignPatientModal
          isOpen={assignModalOpen}
          onClose={() => {
            setAssignModalOpen(false);
            setSelectedPatient(null);
          }}
          patientId={selectedPatient.id}
          patientName={selectedPatient.name}
          currentHospitalId={selectedPatient.hospitalId}
          onAssignmentSuccess={() => {
            if (onPatientListRefresh) {
              onPatientListRefresh();
            }
          }}
        />
      )}
    </div>
  );
}
